import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnimationFrame } from '../useAnimationFrame';
import {
  cueHighlights,
  cueIsActive,
  NowYouTry,
  usePrefersReducedMotion,
  useMountSequence,
  VizCard,
  VIZ_BTN_GHOST,
  VIZ_BTN_PRIMARY,
  type VizCue,
} from '../vizShared';

// TitrationCurve — interactive acid–base titration. A burette drips titrant into
// the analyte while the pH-vs-volume curve plots in real time. Supports four
// chemistries:
//   • strong acid + strong base                (no ka/kb)
//   • weak acid + strong base                  (ka set, titrant 'base')
//   • strong base + strong acid                (titrant 'acid', no kb — mirror)
//   • weak base + strong acid                  (kb set, titrant 'acid')
// The weak-base curve is the true mirror image of the weak-acid curve: it STARTS
// basic (pH from Kb and the base concentration), DROPS through a buffer region
// whose half-equivalence pH = 14 − pKb, crosses an equivalence point on the
// ACIDIC side (the conjugate acid hydrolyzes), and ends low under excess strong
// acid. Shows live moles, concentrations, the equivalence point and an indicator
// color change in the flask.
//
// config: { acid?, base?, ca?, cb?, va?, ka?, kb?, titrant? ('base'|'acid'),
//           analyte? ('acid'|'base'), title? }
//   ca/cb = analyte/titrant molarity, va = analyte volume (mL)
//   ka    = weak-acid constant (analyte is a weak acid when set & >0)
//   kb    = weak-base constant (analyte is a weak base when set & >0)
//   titrant: which species is added from the burette (default 'base')
//   analyte: force which side the analyte is on; otherwise inferred — 'base'
//            when kb>0 or titrant==='acid', else 'acid'
// Resilient: all numeric inputs sanitized; defaults model 0.1 M HCl vs 0.1 M NaOH.

export interface TitrationCurveConfig {
  title?: string;
  acid?: string;
  base?: string;
  ca?: number; // analyte concentration (mol/L)
  cb?: number; // titrant concentration (mol/L)
  va?: number; // analyte volume (mL)
  ka?: number; // if set (and >0), a weak-ACID analyte is modeled
  kb?: number; // if set (and >0), a weak-BASE analyte is modeled
  titrant?: 'base' | 'acid';
  analyte?: 'acid' | 'base';
}

interface Props {
  config?: TitrationCurveConfig;
  onComplete?: (result?: unknown) => void;
  cue?: VizCue;
}

const KW = 1e-14;
const PLOT_W = 300;
const PLOT_H = 220;
const PAD_L = 34;
const PAD_B = 26;
const PAD_T = 10;
const PAD_R = 10;

function pos(n: unknown, fallback: number) {
  const v = typeof n === 'number' ? n : NaN;
  return Number.isFinite(v) && v > 0 ? v : fallback;
}

// Core model: pH after adding `vb` mL of titrant.
//
// analyteSide === 'acid' (titrant is base): strong/weak acid titrated by strong
//   base — the classic rising curve.
// analyteSide === 'base' (titrant is acid): strong/weak base titrated by strong
//   acid — the mirror, falling curve. When `kb` is supplied the base is weak and
//   we model the basic start, the (14 − pKb) buffer plateau, the acidic
//   equivalence point and the strong-acid tail.
function phAt(
  vb: number,
  ca: number,
  cb: number,
  va: number,
  ka: number | null,
  kb: number | null,
  analyteSide: 'acid' | 'base',
): number {
  const molA = ca * (va / 1000); // mol analyte
  const molT = cb * (vb / 1000); // mol titrant added
  const totalV = (va + vb) / 1000; // L
  if (!(totalV > 0)) return 7;

  if (analyteSide === 'acid') {
    // acid analyte, base titrant
    if (ka && ka > 0) {
      // weak acid + strong base buffer / Henderson–Hasselbalch
      const pKa = -Math.log10(ka);
      if (molT < molA - 1e-12) {
        const ha = molA - molT; // remaining acid
        const a = molT; // conjugate base formed
        if (a <= 0) {
          // pure weak acid before any base
          const h = Math.sqrt(ka * (molA / totalV));
          return clampPh(-Math.log10(h));
        }
        return clampPh(pKa + Math.log10(a / ha));
      }
      if (Math.abs(molT - molA) <= 1e-12) {
        // equivalence: solution of conjugate base (hydrolysis)
        const cb2 = molA / totalV;
        const kbConj = KW / ka;
        const oh = Math.sqrt(kbConj * cb2);
        return clampPh(14 + Math.log10(oh));
      }
      // past equivalence: excess strong base dominates
      const excess = (molT - molA) / totalV;
      return clampPh(14 + Math.log10(excess));
    }
    // strong acid + strong base
    const net = molA - molT;
    if (net > 1e-12) return clampPh(-Math.log10(net / totalV));
    if (net < -1e-12) return clampPh(14 + Math.log10(-net / totalV));
    return 7; // exact equivalence
  }

  // analyteSide === 'base': base analyte, strong-acid titrant.
  if (kb && kb > 0) {
    // weak base + strong acid — the mirror of the weak-acid curve.
    const pKb = -Math.log10(kb);
    if (molT < molA - 1e-12) {
      const b = molA - molT; // remaining weak base
      const bh = molT; // conjugate acid formed (BH+)
      if (bh <= 0) {
        // pure weak base before any acid: [OH-] = sqrt(kb·C)
        const oh = Math.sqrt(kb * (molA / totalV));
        return clampPh(14 + Math.log10(oh));
      }
      // buffer region (mirror Henderson–Hasselbalch in pOH):
      // pOH = pKb + log([BH+]/[B]); at half-eq pOH = pKb → pH = 14 − pKb.
      const pOH = pKb + Math.log10(bh / b);
      return clampPh(14 - pOH);
    }
    if (Math.abs(molT - molA) <= 1e-12) {
      // equivalence: solution of the conjugate acid BH+ (hydrolyzes → acidic).
      const cConj = molA / totalV; // [BH+]
      const kaConj = KW / kb;
      const h = Math.sqrt(kaConj * cConj);
      return clampPh(-Math.log10(h));
    }
    // past equivalence: excess strong acid dominates
    const excess = (molT - molA) / totalV;
    return clampPh(-Math.log10(excess));
  }

  // strong base + strong acid (mirror, no kb)
  const net = molA - molT; // remaining base
  if (net > 1e-12) return clampPh(14 + Math.log10(net / totalV));
  if (net < -1e-12) return clampPh(-Math.log10(-net / totalV));
  return 7;
}

function clampPh(p: number) {
  if (!Number.isFinite(p)) return 7;
  return Math.min(14, Math.max(0, p));
}

// A few classic indicators with their transition ranges + endpoint colors.
const INDICATORS = [
  { name: 'Phenolphthalein', lo: 8.2, hi: 10.0, acidColor: 'transparent', baseColor: '#E368A6' },
  { name: 'Methyl orange', lo: 3.1, hi: 4.4, acidColor: '#E05A33', baseColor: '#E8C53C' },
  { name: 'Bromothymol blue', lo: 6.0, hi: 7.6, acidColor: '#E8C53C', baseColor: '#3C7BD6' },
];

// Labeled points along the curve for cue.step.
type StepKey = 'start' | 'pre' | 'equivalence' | 'post';
const STEP_ORDER: StepKey[] = ['start', 'pre', 'equivalence', 'post'];

export default function TitrationCurve({ config = {}, onComplete, cue }: Props) {
  const reduced = usePrefersReducedMotion();
  const hasCue = cueIsActive(cue);

  const ca = pos(config.ca, 0.1);
  const cb = pos(config.cb, 0.1);
  const va = pos(config.va, 25);
  const ka = config.ka && config.ka > 0 ? config.ka : null;
  const kb = config.kb && config.kb > 0 ? config.kb : null;

  // Which side the analyte (in the flask) is on. Explicit `analyte` wins; else a
  // weak base (kb) or an acid titrant implies a base analyte; otherwise acid.
  const analyteSide: 'acid' | 'base' =
    config.analyte === 'base' || config.analyte === 'acid'
      ? config.analyte
      : kb || config.titrant === 'acid'
        ? 'base'
        : 'acid';
  // Titrant is the opposite species; when the analyte is a base the burette
  // delivers acid (honor an explicit titrant only when it is consistent).
  const titrant: 'base' | 'acid' = analyteSide === 'base' ? 'acid' : 'base';
  // The weak analyte constant relevant to the chosen side.
  const weakAcid = analyteSide === 'acid' ? ka : null;
  const weakBase = analyteSide === 'base' ? kb : null;
  const acidName = config.acid ?? (weakAcid ? 'CH₃COOH' : 'HCl');
  const baseName = config.base ?? (weakBase ? 'NH₃' : 'NaOH');

  // Equivalence volume (mL): n_analyte = n_titrant  → vb = ca*va/cb.
  const vEq = (ca * va) / cb;
  const vMax = Math.max(vEq * 2, vEq + 10, 10); // x-axis range

  const [vb, setVb] = useState(0); // current titrant volume added
  const [playing, setPlaying] = useState(false);
  const [indicatorIdx, setIndicatorIdx] = useState(0);
  const vbRef = useRef(0);
  vbRef.current = vb;
  const interactedRef = useRef(false);

  // mL value for each labeled step.
  const stepVol = (k: StepKey): number => {
    switch (k) {
      case 'start': return 0;
      case 'pre': return vEq * 0.5;
      case 'equivalence': return vEq;
      case 'post': return Math.min(vMax, vEq * 1.5);
    }
  };

  const mark = () => {
    if (!interactedRef.current) {
      interactedRef.current = true;
      onComplete?.({ type: 'titration_curve' });
    }
  };

  // ---- Self-demo: auto-drip toward (not past) the equivalence point on mount.
  const demo = useMountSequence(
    [
      { at: 250, run: () => { vbRef.current = 0; setPlaying(true); } },
      { at: 250 + 1500, run: () => setPlaying(false) }, // stop short of equivalence
    ],
    { skip: reduced || hasCue, onSettle: () => setPlaying(false) },
  );

  const cancelDemo = () => {
    if (demo.running) demo.cancel();
    setPlaying(false);
  };

  // ---- Cue handling. progress → volume; play → run drip; step → jump to point.
  useEffect(() => {
    if (cue?.progress == null) return;
    const v = Math.max(0, Math.min(1, cue.progress)) * vMax;
    vbRef.current = v;
    setVb(v);
    setPlaying(false);
  }, [cue?.progress, vMax]);

  useEffect(() => {
    if (cue?.play == null) return;
    if (cue.play && vbRef.current >= vMax) { vbRef.current = 0; setVb(0); }
    setPlaying(Boolean(cue.play));
  }, [cue?.play, vMax]);

  useEffect(() => {
    if (cue?.step == null) return;
    const idx = Math.max(0, Math.min(STEP_ORDER.length - 1, Math.round(cue.step)));
    const v = stepVol(STEP_ORDER[idx]);
    vbRef.current = v;
    setVb(v);
    setPlaying(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cue?.step]);

  // highlight → indicator selection by name.
  useEffect(() => {
    const hi = cueHighlights(cue);
    if (hi.length === 0) return;
    const idx = INDICATORS.findIndex((ind) => hi.some((h) => ind.name.toLowerCase().includes(h)));
    if (idx >= 0) setIndicatorIdx(idx);
  }, [cue]);

  // Auto-add titrant when playing. Slows near the equivalence point so the
  // dramatic vertical jump is visible (real titrations go drop-by-drop there).
  // During the self-demo it stops short of the equivalence point.
  useAnimationFrame((dt) => {
    let next = vbRef.current;
    const near = Math.abs(next - vEq) < vEq * 0.08;
    const rate = (near ? vMax * 0.06 : vMax * 0.35); // mL per second
    next += rate * dt;
    // Self-demo: hold just before the equivalence point.
    const ceiling = demo.running ? Math.min(vMax, vEq * 0.85) : vMax;
    if (next >= ceiling) {
      next = ceiling;
      setPlaying(false);
    }
    vbRef.current = next;
    setVb(next);
    if (!demo.running) mark();
  }, playing);

  // Precompute the full theoretical curve for the backdrop line.
  const curve = useMemo(() => {
    const pts: Array<{ v: number; ph: number }> = [];
    const steps = 240;
    for (let i = 0; i <= steps; i++) {
      const v = (vMax * i) / steps;
      pts.push({ v, ph: phAt(v, ca, cb, va, weakAcid, weakBase, analyteSide) });
    }
    return pts;
  }, [ca, cb, va, weakAcid, weakBase, analyteSide, vMax]);

  const curPh = phAt(vb, ca, cb, va, weakAcid, weakBase, analyteSide);
  const indicator = INDICATORS[indicatorIdx];

  // Flask color: indicator shows base color once pH passes its transition mid.
  const transitioned = curPh >= (indicator.lo + indicator.hi) / 2;
  const flaskColor = transitioned ? indicator.baseColor : (indicator.acidColor === 'transparent' ? '#DCE6F0' : indicator.acidColor);

  // Map (v, pH) → svg coords.
  const sx = (v: number) => PAD_L + (v / vMax) * (PLOT_W - PAD_L - PAD_R);
  const sy = (ph: number) => PAD_T + (1 - ph / 14) * (PLOT_H - PAD_T - PAD_B);

  // The plotted-so-far portion of the curve (up to current vb).
  const drawnPath = useMemo(() => {
    const pts = curve.filter((p) => p.v <= vb + 1e-9);
    if (pts.length === 0) return '';
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.v).toFixed(1)} ${sy(p.ph).toFixed(1)}`).join(' ');
  }, [curve, vb]);

  const fullPath = useMemo(
    () => curve.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.v).toFixed(1)} ${sy(p.ph).toFixed(1)}`).join(' '),
    [curve],
  );

  // Moles bookkeeping.
  const molAnalyte = ca * (va / 1000);
  const molTitrant = cb * (vb / 1000);
  const pct = vEq > 0 ? Math.min(200, (vb / vEq) * 100) : 0;

  const showDrop = playing && !reduced;

  return (
    <VizCard
      reducedMotion={reduced}
      eyebrow="Acid–base titration"
      title={config.title ?? 'Titration curve'}
      subtitle={titrant === 'base' ? `${acidName} titrated with ${baseName}` : `${baseName} titrated with ${acidName}`}
      hint="Drag the burette slider or press Add titrant · ←/→ to adjust mL · auto-drips a demo on load"
      actions={
        <span className="rounded-sm bg-ws-accent-quiet px-2 py-0.5 text-xs font-medium text-ws-accent-ink">
          {weakAcid ? 'Weak acid + strong base' : weakBase ? 'Weak base + strong acid' : 'Strong + strong'}
        </span>
      }
    >
      <div className="w-full select-none">
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start">
          <NowYouTry show={demo.done && !reduced && !hasCue} />
          {/* Burette + flask apparatus */}
          <svg viewBox="0 0 120 220" className="w-24 flex-shrink-0" role="img" aria-label="Burette and flask">
            {/* burette tube */}
            <rect x="50" y="6" width="20" height="120" rx="3" fill="var(--ws-surface-2)" stroke="var(--ws-border)" />
            {/* titrant remaining in burette */}
            <rect
              x="51"
              y={7 + (vb / vMax) * 118}
              width="18"
              height={Math.max(0, 118 - (vb / vMax) * 118)}
              rx="2"
              fill={titrant === 'base' ? '#9BC4E8' : '#E8A89B'}
              opacity="0.85"
              style={{ transition: reduced ? undefined : 'y 200ms linear, height 200ms linear' }}
            />
            {/* stopcock + tip */}
            <rect x="56" y="126" width="8" height="10" fill="var(--ws-ink-4)" />
            <line x1="60" y1="136" x2="60" y2="150" stroke="var(--ws-ink-4)" strokeWidth="2" />
            {/* falling drop while playing */}
            {showDrop && <circle cx="60" cy={152 + ((Date.now() / 4) % 24)} r="2.5" fill={titrant === 'base' ? '#6FA8DC' : '#DC8C7A'} />}
            {/* flask */}
            <path
              d="M44 178 L52 152 L68 152 L76 178 Q80 200 60 200 Q40 200 44 178 Z"
              fill={flaskColor}
              stroke="var(--ws-border-strong)"
              strokeWidth="1.5"
              opacity="0.9"
              style={{ transition: reduced ? undefined : 'fill 400ms ease-out' }}
            />
            <path d="M52 152 L68 152" stroke="var(--ws-border-strong)" strokeWidth="1.5" />
          </svg>

          {/* Live curve */}
          <div className="min-w-0 flex-1">
            <svg viewBox={`0 0 ${PLOT_W} ${PLOT_H}`} className="w-full rounded-md border border-ws-border bg-ws-surface-2" role="img" aria-label="pH versus volume curve">
              {/* gridlines + y labels (pH) */}
              {[0, 2, 4, 6, 8, 10, 12, 14].map((p) => (
                <g key={p}>
                  <line x1={PAD_L} y1={sy(p)} x2={PLOT_W - PAD_R} y2={sy(p)} stroke="var(--ws-border)" strokeWidth="0.5" />
                  <text x={PAD_L - 4} y={sy(p) + 3} textAnchor="end" fontSize="8" fill="var(--ws-ink-3)">{p}</text>
                </g>
              ))}
              <text x={10} y={PAD_T + 4} textAnchor="start" fontSize="7.5" fill="var(--ws-ink-3)">pH</text>
              {/* x labels (mL) */}
              {[0, 0.25, 0.5, 0.75, 1].map((fr) => (
                <text key={fr} x={sx(vMax * fr)} y={PLOT_H - PAD_B + 12} textAnchor="middle" fontSize="8" fill="var(--ws-ink-3)">
                  {(vMax * fr).toFixed(0)}
                </text>
              ))}
              <text x={(PLOT_W + PAD_L) / 2} y={PLOT_H - 2} textAnchor="middle" fontSize="8" fill="var(--ws-ink-3)">Titrant added (mL)</text>

              {/* equivalence vertical guide */}
              <line x1={sx(vEq)} y1={PAD_T} x2={sx(vEq)} y2={PLOT_H - PAD_B} stroke="var(--ws-accent)" strokeWidth="0.75" strokeDasharray="3 3" opacity="0.7" />
              <text x={sx(vEq)} y={PAD_T + 8} textAnchor="middle" fontSize="7.5" fill="var(--ws-accent)">eq {vEq.toFixed(1)} mL</text>

              {/* indicator transition band */}
              <rect x={PAD_L} y={sy(indicator.hi)} width={PLOT_W - PAD_L - PAD_R} height={Math.abs(sy(indicator.lo) - sy(indicator.hi))} fill={indicator.baseColor} opacity="0.12" />

              {/* full theoretical curve (faint) + drawn-so-far (bold) */}
              <path d={fullPath} fill="none" stroke="var(--ws-ink-4)" strokeWidth="1" strokeDasharray="2 3" opacity="0.5" />
              <path d={drawnPath} fill="none" stroke="var(--ws-accent)" strokeWidth="2.25" strokeLinejoin="round" />

              {/* current point */}
              <circle cx={sx(vb)} cy={sy(curPh)} r="4" fill="var(--ws-accent)" stroke="var(--ws-surface)" strokeWidth="1.5" style={{ transition: reduced ? undefined : 'cx 120ms linear, cy 120ms linear' }} />
            </svg>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => { cancelDemo(); if (vb >= vMax) { setVb(0); vbRef.current = 0; } setPlaying((p) => !p); mark(); }}
            className={VIZ_BTN_PRIMARY}
          >
            {playing ? 'Pause' : vb >= vMax ? 'Replay ▶' : 'Add titrant ▶'}
          </button>
          <button
            type="button"
            onClick={() => { cancelDemo(); setVb(0); vbRef.current = 0; setPlaying(false); mark(); }}
            className={VIZ_BTN_GHOST}
          >
            Reset
          </button>
          <input
            type="range"
            min={0}
            max={vMax}
            step={0.1}
            value={vb}
            onPointerDown={cancelDemo}
            onKeyDown={cancelDemo}
            onChange={(e) => { const v = parseFloat(e.target.value); setVb(v); vbRef.current = v; setPlaying(false); mark(); }}
            className="min-w-[120px] flex-1 accent-ws-accent"
            aria-label="Titrant volume in milliliters"
            aria-valuetext={`${vb.toFixed(1)} milliliters`}
          />
          <label className="flex items-center gap-1.5 text-xs text-ws-ink-2">
            Indicator
            <select
              value={indicatorIdx}
              onChange={(e) => { cancelDemo(); setIndicatorIdx(parseInt(e.target.value, 10)); mark(); }}
              className="rounded-sm border border-ws-border bg-ws-tile px-1.5 py-1 text-xs text-ws-ink focus:border-ws-accent focus:outline-none"
              aria-label="Indicator"
            >
              {INDICATORS.map((ind, i) => (
                <option key={ind.name} value={i}>{ind.name}</option>
              ))}
            </select>
          </label>
        </div>

        {/* Live numeric readout */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Volume added" value={`${vb.toFixed(1)} mL`} sub={`${pct.toFixed(0)}% to eq.`} />
          <Stat label="pH" value={curPh.toFixed(2)} accent={flaskColor} />
          <Stat label={`mol ${titrant === 'base' ? 'acid' : 'base'} (analyte)`} value={`${molAnalyte.toExponential(2)}`} />
          <Stat label="mol titrant" value={`${molTitrant.toExponential(2)}`} />
        </div>

        <p className="mt-2 text-xs text-ws-ink-3">
          Analyte: {ca} M × {va} mL · Titrant: {cb} M.{' '}
          {Math.abs(vb - vEq) < vEq * 0.04
            ? 'At the equivalence point the pH jumps sharply — that vertical region is where the indicator flips.'
            : 'Add titrant slowly through the steep region to see the endpoint.'}
        </p>
      </div>
    </VizCard>
  );
}

function Stat({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div className="rounded-md border border-ws-border bg-ws-tile px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-ws-ink-3">{label}</div>
      <div className="font-mono text-sm font-semibold tabular-nums" style={{ color: accent ?? 'var(--ws-ink)' }}>{value}</div>
      {sub && <div className="text-[10px] text-ws-ink-3">{sub}</div>}
    </div>
  );
}
